import { createPortal } from 'react-dom';
import React, { useState, useEffect, useMemo } from 'react';
import { resolveTooltipZIndexForTrigger } from '@/modules/Popup/utils/popupPosition';
import { Body } from '@/modules/Text/components/Body';
import { cn } from '@/shared/utils';
import { TooltipProps } from '@/shared/types/tooltip';
import { useTooltip, useTooltipArrow, useTooltipClasses } from '../../hooks';

/**
 * TooltipComponent - Componente interno de tooltip con posicionamiento avanzado
 *
 * Componente que renderiza el tooltip con posicionamiento dinámico, flecha ajustable
 * y soporte para múltiples posiciones y alineaciones.
 * El copy usa {@link Body} (equivalente a `Text` con `type="body"`) para tokens de
 * tipografía y color en tema claro/oscuro; no se importa `Text` aquí para evitar
 * dependencia circular con el truncado que envuelve `Text`.
 *
 * @internal
 */
const TooltipComponent = ({
  children,
  label,
  position = 'top',
  alignment = 'center',
  id = `tooltip-${Math.random().toString(36).substring(2, 9)}`,
  delay = 0,
  disabled = false,
  className = '',
  fullWidth = false,
  style,
  arrowClassName,
  absolute = false,
  preserveLayout = false,
  ...props
}: TooltipProps) => {
  const {
    isVisible,
    coords,
    adjustedPosition,
    triggerRef,
    tooltipRef,
    containerRef,
    handleMouseEnter,
    handleMouseLeave,
  } = useTooltip(position, alignment, absolute, delay, disabled);

  const tooltipClasses = useTooltipClasses(absolute);
  const arrowStyles = useTooltipArrow(adjustedPosition);

  const tooltipZIndex = isVisible
    ? resolveTooltipZIndexForTrigger(triggerRef.current)
    : undefined;

  // Ajuste dinámico de la flecha: variantes edge (top-left, …) y también top/bottom/left/right
  // cuando `computeTooltipCoords` desplaza el globo por el viewport sin cambiar `adjustedPosition`
  // — la flecha debe seguir apuntando al centro del trigger.
  const [arrowAdjustment, setArrowAdjustment] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (!isVisible || !triggerRef.current || !tooltipRef.current) {
      setArrowAdjustment({});
      return;
    }

    const pos = adjustedPosition;
    const isHorizontalEdge =
      pos === 'top-left' || pos === 'top-right' || pos === 'bottom-left' || pos === 'bottom-right';
    const isVerticalEdge =
      pos === 'left-top' || pos === 'left-bottom' || pos === 'right-top' || pos === 'right-bottom';
    const isTopOrBottom = pos === 'top' || pos === 'bottom';
    const isLeftOrRight = pos === 'left' || pos === 'right';

    if (!isHorizontalEdge && !isVerticalEdge && !isTopOrBottom && !isLeftOrRight) {
      setArrowAdjustment({});
      return;
    }

    requestAnimationFrame(() => {
      if (!triggerRef.current || !tooltipRef.current) return;

      // Usar el hijo directo (el botón real) si existe, ya que triggerRef
      // es el wrapper div del Tooltip y puede tener dimensiones ligeramente distintas
      const firstChild = triggerRef.current.firstElementChild;
      const targetEl = firstChild instanceof HTMLElement ? firstChild : triggerRef.current;
      const targetRect = targetEl.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const arrowSize = 8;
      const minOffset = 8;

      if (isHorizontalEdge || isTopOrBottom) {
        const targetCenterX = targetRect.left + targetRect.width / 2;
        const arrowLeft = targetCenterX - tooltipRect.left - arrowSize / 2;
        const clampedLeft = Math.max(minOffset, Math.min(arrowLeft, tooltipRect.width - minOffset));
        const next: React.CSSProperties = { left: `${clampedLeft}px`, right: 'auto' };
        // Sustituir `left: 50%` + `translateX(-50%)` del estilo base por posición en px
        if (isTopOrBottom) {
          next.transform = 'rotate(45deg)';
        }
        setArrowAdjustment(next);
      } else if (isVerticalEdge || isLeftOrRight) {
        const targetCenterY = targetRect.top + targetRect.height / 2;
        const arrowTop = targetCenterY - tooltipRect.top - arrowSize / 2;
        const clampedTop = Math.max(minOffset, Math.min(arrowTop, tooltipRect.height - minOffset));
        const next: React.CSSProperties = { top: `${clampedTop}px`, bottom: 'auto' };
        if (isLeftOrRight) {
          next.transform = 'rotate(45deg)';
        }
        setArrowAdjustment(next);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tooltipRef/triggerRef son refs, no necesitan estar en deps
  }, [isVisible, adjustedPosition, coords]);

  // Combinar estilos base de la flecha con el ajuste dinámico
  const finalArrowStyles = useMemo(
    () => ({ ...arrowStyles, ...arrowAdjustment }),
    [arrowStyles, arrowAdjustment]
  );

  // Cuando preserveLayout es true, clonamos el children para inyectarle los event handlers
  // y el ref directamente, evitando wrappers que interfieran con el flex layout
  const renderTrigger = () => {
    if (preserveLayout && React.isValidElement(children)) {
      // Clonar el children e inyectarle los event handlers y ref
      return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        ref: triggerRef,
        onMouseEnter: (e: React.MouseEvent) => {
          handleMouseEnter();
          // Llamar al onMouseEnter original si existe
          const originalOnMouseEnter = (children as React.ReactElement<Record<string, unknown>>)
            .props?.onMouseEnter;
          if (originalOnMouseEnter && typeof originalOnMouseEnter === 'function')
            originalOnMouseEnter(e);
        },
        onMouseLeave: (e: React.MouseEvent) => {
          handleMouseLeave();
          // Llamar al onMouseLeave original si existe
          const originalOnMouseLeave = (children as React.ReactElement<Record<string, unknown>>)
            .props?.onMouseLeave;
          if (originalOnMouseLeave && typeof originalOnMouseLeave === 'function')
            originalOnMouseLeave(e);
        },
        onFocus: (e: React.FocusEvent) => {
          handleMouseEnter();
          const originalOnFocus = (children as React.ReactElement<Record<string, unknown>>).props
            ?.onFocus;
          if (originalOnFocus && typeof originalOnFocus === 'function') originalOnFocus(e);
        },
        onBlur: (e: React.FocusEvent) => {
          handleMouseLeave();
          const originalOnBlur = (children as React.ReactElement<Record<string, unknown>>).props
            ?.onBlur;
          if (originalOnBlur && typeof originalOnBlur === 'function') originalOnBlur(e);
        },
      });
    }

    // Comportamiento normal con wrapper
    return (
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        className={cn('ui-inline-block', 'ui-h-fit', fullWidth ? 'ui-w-full' : 'ui-w-fit')}
      >
        {children}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={cn('ui-imj', absolute ? 'ui-relative' : 'ui-contents')}>
      {renderTrigger()}

      {isVisible &&
        (absolute ? (
          <div
            ref={tooltipRef}
            id={id}
            className={cn(tooltipClasses, 'imj-ui-tooltip', className)}
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              opacity: coords.top === 0 && coords.left === 0 ? 0 : 1,
              zIndex: tooltipZIndex,
              ...style,
            }}
            role="tooltip"
            aria-hidden={!isVisible}
            {...props}
          >
            <Body
              size="xs"
              weight="bold"
              color="on-canvas-inverse"
              className="ui-m-0 ui-block ui-p-0 ui-leading-none"
            >
              {label}
            </Body>
            <div className={cn('tooltip-imj-ui-arrow', arrowClassName)} style={finalArrowStyles} />
          </div>
        ) : (
          createPortal(
            <div
              ref={tooltipRef}
              id={id}
              className={cn(tooltipClasses, 'imj-ui-tooltip', className)}
              style={{
                top: `${coords.top}px`,
                left: `${coords.left}px`,
                opacity: coords.top === 0 && coords.left === 0 ? 0 : 1,
                zIndex: tooltipZIndex,
                ...style,
              }}
              role="tooltip"
              aria-hidden={!isVisible}
              {...props}
            >
              <Body
                size="xs"
                weight="bold"
                color="on-canvas-inverse"
                className="ui-m-0 ui-block ui-p-0 ui-leading-none"
              >
                {label}
              </Body>
              <div
                className={cn('tooltip-imj-ui-arrow', arrowClassName)}
                style={finalArrowStyles}
              />
            </div>,
            document.body
          )
        ))}
    </div>
  );
};

/**
 * Tooltip - Componente de tooltip posicionable y configurable
 *
 * Componente de tooltip que se muestra al hacer hover sobre un elemento hijo.
 * Soporta múltiples posiciones, alineaciones, delays y posicionamiento absoluto.
 *
 * @example
 * ```tsx
 * // Tooltip simple
 * <Tooltip label="Este es un tooltip">
 *   <Button>Hover me</Button>
 * </Tooltip>
 *
 * // Tooltip con posición personalizada
 * <Tooltip
 *   label="Tooltip a la derecha"
 *   position="right"
 *   alignment="center"
 * >
 *   <Button>Hover me</Button>
 * </Tooltip>
 *
 * // Tooltip con delay
 * <Tooltip
 *   label="Tooltip con delay"
 *   delay={500}
 * >
 *   <Button>Hover me</Button>
 * </Tooltip>
 * ```
 *
 * @see {@link TooltipProps} para la lista completa de props
 */
export const Tooltip = ({
  children,
  label,
  position = 'top',
  alignment = 'center',
  id = `tooltip-${Math.random().toString(36).substring(2, 9)}`,
  delay = 0,
  disabled = false,
  className = '',
  style,
  arrowClassName,
  absolute = false,
  preserveLayout = false,
  ...props
}: TooltipProps) => {
  if (disabled || !label) {
    return children;
  }

  return (
    <TooltipComponent
      {...props}
      label={label}
      children={children}
      position={position}
      alignment={alignment}
      id={id}
      delay={delay}
      disabled={disabled}
      className={className}
      style={style}
      arrowClassName={arrowClassName}
      absolute={absolute}
      preserveLayout={preserveLayout}
    />
  );
};
